import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { LoginPage } from "./routes/login";
import { DashboardPage } from "./routes/dashboard";
import { CustomersListPage } from "./routes/customers/list";
import { CustomerDetailPage } from "./routes/customers/detail";
import { Cliente360ListPage } from "./routes/cliente360/list";
import { Cliente360DetailPage } from "./routes/cliente360/detail";
import { AudienciasPage } from "./routes/audiencias/AudienciasPage";
import { ArticulosConfigPage } from "./routes/articulos-config";
import { PasarelasPagoPage } from "./routes/pasarelas-pago";
import { DeliveryAdminPage } from "./routes/delivery";
import ReservasList from "./routes/reservas/list";
import ReservasDetail from "./routes/reservas/detail";
import PublicReservar from "./routes/reservar/public";
import ReservaStatusPage from "./routes/reservar/status";
import MisReservasPage from "./routes/reservar/mis-reservas";
import WalkinPublicPage from "./routes/walkin/public";
import WalkinPage from "./routes/tienda/walkin";
import EventosCatalogoPage from "./routes/eventos/public";
import EventoDetallePage from "./routes/eventos/detalle";
import EventoCompraStatusPage from "./routes/eventos/compra-status";
import EventoCheckinPage from "./routes/eventos/checkin";
import { EventosPublicosPage } from "./routes/tienda/eventos-publicos";
import ResponderEncuestaPage from "./routes/encuestas/responder";
import ResponderEncuestaQrPage from "./routes/encuestas/responder-qr";
import MisEncuestasPage from "./routes/encuestas/mis-respuestas";
import ReportesPage from "./routes/reportes";
import CalendarioPage from "./routes/calendario";
import TiendaDashboard from "./routes/tienda";
import UsuariosPage from "./routes/usuarios";
import SincronizacionPage from "./routes/sincronizacion";
import { MenuPublicoPage } from "./routes/menu-publico";
import { MenuConfigPage } from "./routes/tienda/menu-config";
import { CategoriasPage } from "./routes/tienda/categorias";
import { ListaEsperaPage } from "./routes/tienda/lista-espera";
import { PagosReservasPage } from "./routes/tienda/pagos-reservas";
import { BloqueosPage } from "./routes/tienda/bloqueos";
import EncuestasPage from "./routes/tienda/encuestas";
import EncuestasQrPage from "./routes/encuestas-qr";
import { EmailTemplatesPage } from "./routes/tienda/email-templates";
import { QrCodesPage } from "./routes/tienda/qr-codes";
import { EventosPage } from "./routes/tienda/eventos";
import OcupacionPage from "./routes/tienda/ocupacion";
import TareasPage from "./routes/tienda/tareas";
import { WhatsAppConfigPage } from "./routes/tienda/whatsapp";
import { DeliveryZonasPage } from "./routes/tienda/delivery-zonas";
import { ModulosPage } from "./routes/tienda/modulos";
import { ModulosGlobalPage } from "./routes/admin/modulos-global";
import SucursalesAdmin from "./routes/sucursales/index";
import SmtpConfigPage from "./routes/smtp-config";
import { TiendaLayout } from "./components/TiendaLayout";
import CheckInPage from "./routes/checkin";
import ConfirmarReservaPage from "./routes/confirmar-reserva";
import PerfilPage from "./routes/perfil";
import DiagnosticosPage from "./routes/diagnosticos";
import { useAuth, AuthUser } from "./lib/auth";
import { esMaestro, puedeReportes, puedeClientes, puedeConfigurarSucursal, puedeVerReservas, puedeOperarNegocio, puedeVerEncuestaQr, esRepartidor, rutaInicio, accesoEfectivo, puedeGestionarUsuarios } from "./lib/permisos";
import RepartidorPage from "./routes/repartidor";
import { ScrollToTop } from "./components/ScrollToTop";
import { QuickNavFab } from "./components/QuickNavFab";

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { token } = useAuth();
  if (!token) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

// Rutas con :id cuyo override se define sobre la ruta BASE de la tarjeta
// (ej. /reservas/42 -> clave "/reservas"), no por id puntual.
const BASES_CON_ID = ["/reservas", "/cliente360", "/customers"];
function rutaParaOverride(pathname: string): string {
  const base = pathname.replace(/\/[^/]+$/, "");
  return BASES_CON_ID.includes(base) ? base : pathname;
}

function RoleRoute({ allow, children }: { allow: (u: AuthUser | null) => boolean; children: React.ReactNode }) {
  const { token, user } = useAuth();
  const location = useLocation();
  if (!token) return <Navigate to="/login" replace />;
  const ruta = rutaParaOverride(location.pathname);
  if (!accesoEfectivo(user, ruta, allow(user))) return <Navigate to={rutaInicio(user)} replace />;
  return <>{children}</>;
}

// Layout compartido con campana de notificaciones
function TiendaRoute({ children }: { children: React.ReactNode }) {
  const { token } = useAuth();
  if (!token) return <Navigate to="/login" replace />;
  return <TiendaLayout>{children}</TiendaLayout>;
}

function Home() {
  const { user } = useAuth();
  if (esRepartidor(user)) return <Navigate to="/repartidor" replace />;
  if (esMaestro(user)) return <DashboardPage />;
  return <Navigate to="/tienda" replace />;
}

export default function App() {
  return (
    <>
      <ScrollToTop />
      <QuickNavFab />
    <Routes>
      {/* Públicas sin login */}
      <Route path="/reservar" element={<PublicReservar />} />
      <Route path="/lista-espera" element={<WalkinPublicPage />} />
      <Route path="/eventos" element={<EventosCatalogoPage />} />
      <Route path="/eventos/compra/:id" element={<EventoCompraStatusPage />} />
      <Route path="/eventos/:id" element={<EventoDetallePage />} />
      <Route path="/checkin-evento/:token" element={<EventoCheckinPage />} />
      <Route path="/reserva/:id" element={<ReservaStatusPage />} />
      <Route path="/mis-reservas" element={<MisReservasPage />} />
      <Route path="/encuesta/:token" element={<ResponderEncuestaPage />} />
      <Route path="/encuesta-qr/:token" element={<ResponderEncuestaQrPage />} />
      <Route path="/mis-encuestas" element={<MisEncuestasPage />} />
      <Route path="/menu/sucursal/:sucursalId" element={<MenuPublicoPage />} />
      <Route path="/menu/sucursal/:sucursalId/mesa/:mesa" element={<MenuPublicoPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/" element={<ProtectedRoute><Home /></ProtectedRoute>} />

      {/* Tienda — todas usan TiendaLayout (header con campana) */}
      <Route path="/tienda" element={<TiendaRoute><TiendaDashboard /></TiendaRoute>} />
      <Route path="/tienda/menu-config" element={<RoleRoute allow={puedeConfigurarSucursal}><TiendaLayout><MenuConfigPage /></TiendaLayout></RoleRoute>} />
      <Route path="/tienda/categorias" element={<RoleRoute allow={puedeConfigurarSucursal}><TiendaLayout><CategoriasPage /></TiendaLayout></RoleRoute>} />
      <Route path="/tienda/lista-espera" element={<RoleRoute allow={puedeVerReservas}><TiendaLayout><ListaEsperaPage /></TiendaLayout></RoleRoute>} />
      <Route path="/tienda/walkin" element={<RoleRoute allow={puedeOperarNegocio}><TiendaLayout><WalkinPage /></TiendaLayout></RoleRoute>} />
      <Route path="/tienda/eventos-publicos" element={<RoleRoute allow={puedeOperarNegocio}><TiendaLayout><EventosPublicosPage /></TiendaLayout></RoleRoute>} />
      <Route path="/tienda/pagos-reservas" element={<RoleRoute allow={puedeVerReservas}><TiendaLayout><PagosReservasPage /></TiendaLayout></RoleRoute>} />
      <Route path="/tienda/bloqueos" element={<RoleRoute allow={puedeConfigurarSucursal}><TiendaLayout><BloqueosPage /></TiendaLayout></RoleRoute>} />
      <Route path="/tienda/encuestas" element={<RoleRoute allow={puedeConfigurarSucursal}><TiendaLayout><EncuestasPage /></TiendaLayout></RoleRoute>} />
      <Route path="/encuestas-qr" element={<RoleRoute allow={puedeVerEncuestaQr}><TiendaLayout><EncuestasQrPage /></TiendaLayout></RoleRoute>} />
      <Route path="/tienda/email-templates" element={<RoleRoute allow={puedeConfigurarSucursal}><TiendaLayout><EmailTemplatesPage /></TiendaLayout></RoleRoute>} />
      <Route path="/tienda/qr" element={<RoleRoute allow={puedeOperarNegocio}><TiendaLayout><QrCodesPage /></TiendaLayout></RoleRoute>} />
      <Route path="/tienda/eventos" element={<TiendaRoute><EventosPage /></TiendaRoute>} />
      <Route path="/tienda/tareas" element={<TiendaRoute><TareasPage /></TiendaRoute>} />
      <Route path="/tienda/whatsapp" element={<RoleRoute allow={puedeConfigurarSucursal}><TiendaLayout><WhatsAppConfigPage /></TiendaLayout></RoleRoute>} />
      <Route path="/tienda/delivery-zonas" element={<RoleRoute allow={puedeConfigurarSucursal}><TiendaLayout><DeliveryZonasPage /></TiendaLayout></RoleRoute>} />
      <Route path="/tienda/modulos" element={<RoleRoute allow={(u) => esMaestro(u) || u?.role === "gerencia"}><TiendaLayout><ModulosPage /></TiendaLayout></RoleRoute>} />
      <Route path="/admin/modulos" element={<RoleRoute allow={(u) => !!u?.superAdmin}><TiendaLayout><ModulosGlobalPage /></TiendaLayout></RoleRoute>} />
      <Route path="/tienda/ocupacion" element={<TiendaRoute><OcupacionPage /></TiendaRoute>} />
      <Route path="/perfil" element={<ProtectedRoute><PerfilPage /></ProtectedRoute>} />
      <Route path="/repartidor" element={<RoleRoute allow={esRepartidor}><RepartidorPage /></RoleRoute>} />
      <Route path="/admin/diagnosticos" element={<RoleRoute allow={esMaestro}><DiagnosticosPage /></RoleRoute>} />
      <Route path="/checkin/:token" element={<CheckInPage />} />
      <Route path="/confirmar-reserva/:token" element={<ConfirmarReservaPage />} />
      <Route path="/calendario" element={<TiendaRoute><CalendarioPage /></TiendaRoute>} />
      <Route path="/reservas" element={<RoleRoute allow={puedeVerReservas}><TiendaLayout><ReservasList /></TiendaLayout></RoleRoute>} />
      <Route path="/reservas/:id" element={<RoleRoute allow={puedeVerReservas}><TiendaLayout><ReservasDetail /></TiendaLayout></RoleRoute>} />
      <Route path="/reportes" element={<RoleRoute allow={puedeReportes}><TiendaLayout><ReportesPage /></TiendaLayout></RoleRoute>} />
      <Route path="/customers" element={<RoleRoute allow={puedeClientes}><TiendaLayout><CustomersListPage /></TiendaLayout></RoleRoute>} />
      <Route path="/customers/:id" element={<RoleRoute allow={puedeClientes}><TiendaLayout><CustomerDetailPage /></TiendaLayout></RoleRoute>} />
      <Route path="/cliente360" element={<RoleRoute allow={puedeClientes}><TiendaLayout><Cliente360ListPage /></TiendaLayout></RoleRoute>} />
      <Route path="/cliente360/:id" element={<RoleRoute allow={puedeClientes}><TiendaLayout><Cliente360DetailPage /></TiendaLayout></RoleRoute>} />
      <Route path="/audiencias" element={<RoleRoute allow={puedeClientes}><TiendaLayout><AudienciasPage /></TiendaLayout></RoleRoute>} />
      <Route path="/articulos-config" element={<RoleRoute allow={puedeConfigurarSucursal}><TiendaLayout><ArticulosConfigPage /></TiendaLayout></RoleRoute>} />
      <Route path="/pasarelas-pago" element={<RoleRoute allow={puedeConfigurarSucursal}><TiendaLayout><PasarelasPagoPage /></TiendaLayout></RoleRoute>} />
      <Route path="/delivery" element={<RoleRoute allow={puedeClientes}><TiendaLayout><DeliveryAdminPage /></TiendaLayout></RoleRoute>} />

      {/* Solo admin / super admin */}
      <Route path="/sucursales" element={<RoleRoute allow={puedeConfigurarSucursal}><SucursalesAdmin /></RoleRoute>} />
      <Route path="/smtp-config" element={<RoleRoute allow={esMaestro}><SmtpConfigPage /></RoleRoute>} />
      <Route path="/usuarios" element={<RoleRoute allow={puedeGestionarUsuarios}><UsuariosPage /></RoleRoute>} />
      <Route path="/sincronizacion" element={<RoleRoute allow={esMaestro}><SincronizacionPage /></RoleRoute>} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </>
  );
}
